import * as Sentry from '@sentry/react';
import { Alert, AppBar, Box, Button, CircularProgress, Container, Divider, Stack, Tab, Tabs, TextField, Toolbar, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import api, { setDebugUid, getDebugUid, setTokenProvider } from './api/client';
import { useAuth } from './auth/AuthProvider';
import { ShellPanel } from './features/shell/ShellPanel';
import { TaskInspector } from './features/tasks/TaskInspector';
import { CaseInspector } from './features/cases/CaseInspector';
import { AssetInspector } from './features/assets/AssetInspector';
import { SaveInspector } from './features/saves/SaveInspector';
import { AuditInspector } from './features/audit/AuditInspector';
import { PlayerInspector } from './features/players/PlayerInspector';
import { UserInspector } from './features/users/UserInspector';

const tabs = [
  { label: 'Tasks', path: '/tasks' },
  { label: 'Cases', path: '/cases' },
  { label: 'Assets', path: '/assets' },
  { label: 'Saves', path: '/saves' },
  { label: 'Players', path: '/players' },
  { label: 'Users', path: '/users' },
  { label: 'Audit', path: '/audit' },
  { label: 'Shell', path: '/shell' },
] as const;

const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);

export default function App() {
  const { user, loading, isFirebase, getToken, signInWithGoogle, signInWithEmail, signOut } = useAuth();
  const [uid, setUid] = useState(getDebugUid());
  const location = useLocation();
  const navigate = useNavigate();

  const currentTab = tabs.findIndex((t) => location.pathname.startsWith(t.path));

  const [sessionReady, setSessionReady] = useState(!isFirebase);
  const [sessionError, setSessionError] = useState('');

  // Wire the token provider and bootstrap the server-side session after Firebase auth resolves.
  useEffect(() => {
    if (!isFirebase) return;
    setTokenProvider(getToken);
    if (!user) return;
    let cancelled = false;
    // Small delay to ensure the token provider is wired before the interceptor runs.
    const run = async () => {
      try {
        await api.post('/api/player/bootstrap');
        if (!cancelled) setSessionReady(true);
      } catch (err: unknown) {
        if (!cancelled) {
          const status = (err as { response?: { status?: number } })?.response?.status;
          setSessionError(status === 401
            ? 'Not authorized. Your account may not have access.'
            : 'Failed to connect to the server.');
        }
      }
    };
    run();
    return () => { cancelled = true; };
  }, [isFirebase, user, getToken]);

  function handleUidChange(value: string) {
    setUid(value);
    setDebugUid(value);
  }

  // Loading spinner while Firebase resolves initial auth state.
  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#101114', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  // Firebase is active but user is not signed in — show login.
  if (isFirebase && !user) {
    return <LoginScreen signInWithGoogle={signInWithGoogle} signInWithEmail={signInWithEmail} />;
  }

  // Wait for server-side session bootstrap.
  if (isFirebase && !sessionReady) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#101114', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 2 }}>
        {sessionError
          ? <Alert severity="error">{sessionError}</Alert>
          : <CircularProgress color="primary" />}
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#101114', color: '#f3f3f3' }}>
      <AppBar position="static" color="transparent" elevation={0}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>Black Ledger Admin</Typography>
          {isFirebase ? (
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="body2" color="text.secondary">{user?.email}</Typography>
              <Button size="small" variant="outlined" onClick={signOut}>Sign out</Button>
            </Stack>
          ) : (
            <TextField
              label="Debug UID"
              value={uid}
              onChange={(e) => handleUidChange(e.target.value)}
              size="small"
              sx={{ width: 200 }}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          )}
        </Toolbar>
      </AppBar>
      <Container sx={{ py: 2 }}>
        <Stack spacing={2}>
          <Tabs
            value={currentTab === -1 ? 0 : currentTab}
            onChange={(_, v) => navigate(tabs[v].path)}
            textColor="primary"
            indicatorColor="primary"
          >
            {tabs.map((t) => <Tab key={t.path} label={t.label} />)}
          </Tabs>
          <SentryRoutes>
            <Route path="/tasks" element={<TaskInspector />} />
            <Route path="/cases" element={<CaseInspector />} />
            <Route path="/assets" element={<AssetInspector />} />
            <Route path="/saves" element={<SaveInspector />} />
            <Route path="/players" element={<PlayerInspector />} />
            <Route path="/users" element={<UserInspector />} />
            <Route path="/audit" element={<AuditInspector />} />
            <Route path="/shell" element={<ShellPanel />} />
            <Route path="*" element={<Navigate to="/tasks" replace />} />
          </SentryRoutes>
        </Stack>
      </Container>
    </Box>
  );
}

function LoginScreen({ signInWithGoogle, signInWithEmail }: {
  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function handleEmailSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setBusy(true);
    setError('');
    try {
      await signInWithEmail(email.trim(), password);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Sign-in failed');
    } finally {
      setBusy(false);
    }
  }

  async function handleGoogle() {
    setError('');
    try {
      await signInWithGoogle();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Sign-in failed');
    }
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#101114', color: '#f3f3f3', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Stack spacing={3} alignItems="center" sx={{ width: 320 }}>
        <Typography variant="h4">Black Ledger Admin</Typography>
        <Stack component="form" spacing={2} onSubmit={handleEmailSubmit} sx={{ width: '100%' }}>
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            size="small"
            fullWidth
            autoFocus
          />
          <TextField
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            size="small"
            fullWidth
          />
          <Button type="submit" variant="contained" disabled={busy || !email.trim() || !password}>
            Sign in with Email
          </Button>
        </Stack>
        <Divider sx={{ width: '100%' }}>or</Divider>
        <Button variant="outlined" fullWidth onClick={handleGoogle}>Sign in with Google</Button>
        {error && <Alert severity="error" sx={{ width: '100%' }}>{error}</Alert>}
      </Stack>
    </Box>
  );
}
